/**
 * QC Report — File.
 *
 * Two questions about an import: what did the mapping steps decide, and how
 * much of what could have been measured did the file actually contain.
 *
 * Density maths lives in `lib/sparseness.ts` (pure, unit-tested); this only
 * renders it.
 */
import { useMemo } from "react";

import { Info } from "lucide-react";

import { TabEmpty, TabError, TabLoading } from "./TabState";
import { useAppDispatch } from "../../Store/Hooks";
import { fetchDataHubQcFileReport } from "../../Store/Reducers/DataHubConsoleSlice";
import { useAdapterData } from "../hooks/useAdapterData";
import { computeSparseness, type SparsenessRow } from "../lib/sparseness";

const BRAND = "#974ae4";

function pct(v: number): string {
  return `${(v * 100).toFixed(1)}%`;
}

/** Product-style density track — purple fill; amber when coverage exceeds 100%. */
function DensityBar({ density }: { density: number }) {
  const over = density > 1;
  const width = `${Math.min(100, Math.max(0, density * 100))}%`;
  return (
    <div
      className="relative h-1.5 w-full overflow-hidden rounded-full bg-purple-100"
      role="progressbar"
      aria-valuenow={Math.round(density * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-300"
        style={{
          width,
          backgroundColor: over ? "#f59e0b" : BRAND,
        }}
      />
    </div>
  );
}

function Factor({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="min-w-0 rounded-[12px] border border-gray-200 bg-gray-50/60 px-3 py-2">
      <p className="m-0 text-[11px] font-medium text-gray-500">{label}</p>
      <p className="m-0 mt-0.5 text-lg font-semibold tabular-nums leading-tight text-gray-900">
        {value}
      </p>
    </div>
  );
}

/**
 * One mapping step's outcome. Absent counts render "—" rather than 0: a step
 * that was never recorded is not the same as one that decided nothing.
 */
function MappingCard({
  title,
  rows,
}: {
  title: string;
  rows: { label: string; value: number | undefined }[];
}) {
  const recorded = rows.some(r => typeof r.value === "number");
  return (
    <div className="rounded-[12px] border border-gray-200 bg-white p-3">
      <p className="m-0 mb-2 text-sm font-semibold text-gray-900">{title}</p>
      {recorded ? (
        <ul className="m-0 flex w-full list-none p-0">
          {rows.map(r => (
            <li key={r.label} className="min-w-0 flex-1">
              <p className="m-0 text-[11px] text-gray-500">{r.label}</p>
              <p className="m-0 text-sm font-semibold tabular-nums text-gray-900">
                {typeof r.value === "number" ? r.value : "—"}
              </p>
            </li>
          ))}
        </ul>
      ) : (
        <p className="m-0 text-xs italic text-gray-500">
          Not recorded for this run.
        </p>
      )}
    </div>
  );
}

function DensityTable({
  title,
  hint,
  rows,
}: {
  title: string;
  hint: string;
  rows: SparsenessRow[];
}) {
  return (
    <div className="rounded-[12px] border border-gray-200 bg-white p-3">
      <div className="mb-2.5 flex items-start gap-1.5">
        <div className="min-w-0 flex-1">
          <p className="m-0 flex items-center gap-1 text-sm font-semibold text-gray-900">
            {title}
            <span title={hint} className="inline-flex text-gray-400">
              <Info className="h-3.5 w-3.5" aria-hidden />
              <span className="sr-only">{hint}</span>
            </span>
          </p>
          <p className="m-0 mt-0.5 text-[11px] text-gray-500">
            {rows.length} row{rows.length === 1 ? "" : "s"}
          </p>
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="m-0 text-xs text-gray-500">Nothing to show.</p>
      ) : (
        <div className="max-h-[280px] space-y-2.5 overflow-auto pr-0.5">
          {rows.map(r => (
            <div key={r.key} className="min-w-0 space-y-1">
              <p
                className="m-0 truncate text-xs font-medium text-gray-800"
                title={r.label}
              >
                {r.label}
              </p>
              <DensityBar density={r.density} />
              <p className="m-0 text-[11px] tabular-nums text-gray-500">
                <span className="font-semibold text-gray-800">
                  {pct(r.density)}
                </span>
                <span className="mx-1 text-gray-300">·</span>
                {r.actual} / {r.max}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function QcFileReport({
  runId,
  batchId,
  infoHint = "Run scopes the File and Audit reports. Production is org-wide. What QC found in this import, row by row.",
}: {
  runId?: string;
  batchId?: number;
  /** Shown on the Mapping summary info icon hover. */
  infoHint?: string;
}) {
  const dispatch = useAppDispatch();
  const { data, loading, error } = useAdapterData(
    () =>
      dispatch(
        fetchDataHubQcFileReport({ run_id: runId, batch_id: batchId })
      ).unwrap(),
    [runId, batchId]
  );

  const report = useMemo(
    () => (data ? computeSparseness(data.rubric, data.rows) : null),
    [data]
  );

  if (loading) return <TabLoading />;
  if (error) return <TabError error={error} />;
  if (!report || !data) return null;

  if (data.rows.length === 0) {
    return (
      <TabEmpty
        message={
          runId
            ? "No staged rows for this run’s import batch."
            : "No staged rows to report on. Run an import first."
        }
      />
    );
  }

  const d = data.decisions;
  const f = report.factors;

  return (
    <div className="space-y-4">
      <section className="space-y-2">
        <div>
          <h3 className="m-0 flex items-center gap-1.5 text-sm font-semibold text-gray-900">
            Mapping summary
            <span
              title={infoHint}
              className="inline-flex cursor-help text-gray-400 hover:text-gray-500"
            >
              <Info className="h-3.5 w-3.5" aria-hidden />
              <span className="sr-only">{infoHint}</span>
            </span>
          </h3>
          <p className="m-0 mt-0.5 text-xs text-gray-500">
            How columns, metrics, and accounts were resolved for this import.
          </p>
        </div>
        <div className="grid gap-2.5 md:grid-cols-3">
          <MappingCard
            title="Column mapping"
            rows={[
              { label: "Auto-matched", value: d.columns?.auto_matched },
              { label: "User-matched", value: d.columns?.user_matched },
              { label: "Ignored", value: d.columns?.ignored },
            ]}
          />
          <MappingCard
            title="Metric ID mapping"
            rows={[
              { label: "Auto-matched", value: d.metrics?.auto_matched },
              { label: "User-matched", value: d.metrics?.user_matched },
              { label: "Skipped", value: d.metrics?.skipped },
            ]}
          />
          <MappingCard
            title="Account mapping"
            rows={[
              { label: "Auto-matched", value: d.accounts?.auto_matched },
              { label: "User-matched", value: d.accounts?.user_matched },
              { label: "Skipped", value: d.accounts?.skipped },
            ]}
          />
        </div>
      </section>

      <section className="space-y-2.5 rounded-[12px] border border-gray-200 bg-white p-3.5">
        <div>
          <h3 className="m-0 text-sm font-semibold text-gray-900">
            Sparseness analysis
          </h3>
          <p className="m-0 mt-0.5 text-xs leading-relaxed text-gray-500">
            Data density = actual data points ÷ maximum possible. The rubric
            excludes the &quot;Relationships Alignment&quot; category.
          </p>
        </div>

        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <Factor label="Metrics in rubric" value={f.metrics_in_rubric} />
          <Factor label="Categories" value={f.categories_in_rubric} />
          <Factor label="Accounts in file" value={f.accounts_in_file} />
          <Factor label="Applied dates" value={f.distinct_dates_in_file} />
        </div>

        <div className="space-y-1.5 rounded-[12px] border border-purple-100 bg-purple-50/40 px-3 py-2.5">
          <p className="m-0 text-[11px] font-medium text-gray-500">
            Overall density
          </p>
          <p className="m-0 text-2xl font-semibold tabular-nums leading-none text-gray-900">
            {pct(report.overall.density)}
          </p>
          <p className="m-0 text-[11px] tabular-nums text-gray-500">
            {report.overall.actual.toLocaleString()} /{" "}
            {report.overall.max.toLocaleString()} points
            {report.overall.density > 1 ? " · above 100% coverage" : ""}
          </p>
          <DensityBar density={report.overall.density} />
        </div>

        <div className="grid gap-2.5 lg:grid-cols-2">
          <DensityTable
            title="Density by metric"
            hint="Points for this metric ÷ (accounts × dates)"
            rows={report.by_metric}
          />
          <DensityTable
            title="Density by applied date"
            hint="Points on this date ÷ (metrics × accounts)"
            rows={report.by_date}
          />
          <DensityTable
            title="Density by category"
            hint="Points in this category ÷ (its metrics × accounts × its dates)"
            rows={report.by_category}
          />
          <DensityTable
            title="Density by account"
            hint="Points for this account ÷ (metrics × dates)"
            rows={report.by_account}
          />
        </div>
      </section>
    </div>
  );
}

export default QcFileReport;
